import type { InferSessionFromClient, InferUserFromClient, ClientOptions } from 'better-auth/client'
import { createAuthClient } from 'better-auth/client'
import { magicLinkClient } from "better-auth/client/plugins";

// Minimal shape of a Vue ref (or Nuxt useState) - anything with a `.value`
// property. Kept as a structural type (not `import type { Ref } from 'vue'`)
// so this function has zero framework dependencies and can be unit tested
// with plain `{ value }` objects, no Vue/Nuxt runtime required.
type RefLike<T> = { value: T }

/**
 * Pure decision logic behind `useAuth().fetchSession()`, extracted so it can
 * be unit tested without standing up a Nuxt app (useState/useRuntimeConfig/
 * ref/computed are Nuxt auto-imports the composable relies on, which aren't
 * available in a plain vitest run). `useAuth()` is a thin caller around this.
 *
 * Network failures (offline, unreachable auth backend, etc.) from
 * `getSession` must NOT reject out of this function: an uncaught rejection
 * here throws inside a Nuxt plugin (00.auth.server.ts / 01.auth.client.ts
 * run on every route), which Nuxt turns into a fatal NUXT_E1005 error and
 * replaces the entire page - including the offline fallback page itself -
 * with its generic error page. Fail closed (no session) instead.
 *
 * `sessionFetching` must be reset to `false` on every path out of this
 * function (success, thrown error, or normal completion) - pre-fix, a throw
 * left it stuck `true` forever, permanently blocking later fetches for that
 * page load.
 */
export async function performFetchSession(
    sessionFetching: RefLike<boolean>,
    session: RefLike<any>,
    user: RefLike<any>,
    getSession: () => Promise<{ data: { session?: unknown; user?: unknown } | null }>
) {
    if (sessionFetching.value) {
        console.log('Already fetching user session')
        return
    }
    sessionFetching.value = true
    try {
        const { data } = await getSession()
        session.value = data?.session || null
        user.value = data?.user || null
        return data
    } catch (error) {
        console.error('Failed to fetch auth session:', error)
        session.value = null
        user.value = null
        return null
    } finally {
        sessionFetching.value = false
    }
}

export const useAuth = () => {
    const config = useRuntimeConfig()    
    const headers = import.meta.server ? useRequestHeaders() : undefined
    
    const client = createAuthClient({
        baseURL: config.public.clientFrontend || 'https://localhost:3001',
        plugins: [
            magicLinkClient()
        ]
    })
    
    const session = useState<InferSessionFromClient<ClientOptions> | null>('auth:session', () => null)
    const user = useState<InferUserFromClient<ClientOptions> | null>('auth:user', () => null)
    const sessionFetching = import.meta.server ? ref(false) : useState('auth:sessionFetching', () => false)    
    
    // Check if user is logged in
    const isLoggedIn = computed(() => !!user.value);

    // Get the current user
    // Sign out method
    //
    // Purges the SW's 'ohc-pages' cache (app/sw.ts's NavigationRoute) after
    // sign-out: SSR renders embed the requesting user's session/user state
    // (00.auth.server.ts's fetchSession() call on every render), so cached
    // page HTML can contain PII for whoever was logged in when a route was
    // last cached. This is the actual logout path exercised by every caller
    // in the app (header.vue, my-ohc-nav.vue, turquoise-alert.vue,
    // tours-list.vue, itineraries.vue, archives.vue all call useAuth().signOut()
    // directly) — stores/user.ts's onUserLogout exists but is never invoked
    // anywhere, so purging there would be dead code.
    //
    // Residual: this only fires on an explicit logout. A user who stays
    // logged in and never signs out keeps their own cached pages on their own
    // device for up to the ExpirationPlugin's maxAgeSeconds (24h, see sw.ts) —
    // that's a same-user/same-device exposure window, not a cross-user leak,
    // and is the accepted tradeoff for not adding a time-based purge loop.
    //
    // Guarded for no-SW environments: 'caches' is undefined outside a
    // browser with the CacheStorage API (SSR, older/locked-down browsers,
    // some in-app webviews), and caches.delete() is a no-op harmlessly
    // resolving `false` if the SW never created 'ohc-pages' in the first
    // place (e.g. PWA disabled via the kill switch, or first-ever visit).
    const signOut = async () => {
        await client.signOut();
        if (import.meta.client && typeof caches !== 'undefined') {
            try {
                await caches.delete('ohc-pages');
            } catch (error) {
                console.error('Failed to purge ohc-pages cache on sign-out:', error);
            }
        }
    };

    const fetchSession = async () =>
        performFetchSession(sessionFetching, session, user, () =>
            client.getSession({
                fetchOptions: {
                    headers,
                },
            })
        )

    if (import.meta.client) {
        client.$store.listen('$sessionSignal', async (signal) => {
        if (!signal) return
            await fetchSession()
        })
    }

    return {
        session,
        isLoggedIn,
        user,
        client, 
        fetchSession,
        signOut
    };
};